import {ChangeDetectorRef, Component, computed, effect, signal, Signal, WritableSignal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {filter, map, shareReplay, tap} from 'rxjs/operators';
import {Apollo, onlyCompleteData, QueryRef} from 'apollo-angular';
import {ActivatedRoute, Router} from '@angular/router';
import {Folder, Organization, Query} from '../../../types-repository';
import {FILE_FOLDER_LIST_WITH_ARCHIVE, MKDIR} from '../../../queries-repository';
import {BannerService} from '../../../banner/banner.service';
import {BreadCrumb, BreadCrumbs, LogoAreaComponent} from '../../logo-area/logo-area.component';
import {ContextService} from '../../../services/context.service';
import {PoMetaService} from '../../../services/po-meta.service';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import { AsyncPipe } from '@angular/common';
import {FolderListingComponent} from '../folder-listing/folder-listing.component';
import {FolderAssetViewComponent} from '../folder-asset-view/folder-asset-view.component';
import {ClTabsModule} from '../../cl-tabs/cl-tabs.module';
import {DefaultPoComponent} from '../../submit-po/default-po/default-po.component';
import {VodDefaultPoComponent} from '../../submit-po/vod-default-po/vod-default-po.component';
import {VodRepackagingPoComponent} from '../../submit-po/vod-repackaging-po/vod-repackaging-po.component';
import {VodDisplayInteractivePoComponent} from '../../submit-po/vod-display-interactive-po/vod-display-interactive-po.component';
import {VodPassthroughPoComponent} from '../../submit-po/vod-passthrough-po/vod-passthrough-po.component';
import {IsAllowedAnywherePipe, IsAllowedPipe} from '../../../pipes/has-action-pipe';
import {Airline} from '../../../types-workflow';
import {switchMap} from 'rxjs';
import {Dialog} from '@angular/cdk/dialog';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';


@Component({
  selector: 'app-folder',
  templateUrl: './folder.component.html',
  styleUrls: ['./folder.component.scss'],
  imports: [
    LogoAreaComponent,
    HeadlineSectionComponent,
    FolderListingComponent,
    FolderAssetViewComponent,
    ClTabsModule,
    DefaultPoComponent,
    VodDefaultPoComponent,
    VodRepackagingPoComponent,
    VodDisplayInteractivePoComponent,
    VodPassthroughPoComponent,
    IsAllowedPipe,
    IsAllowedAnywherePipe,
]
})
export class FolderComponent {
  organization: Signal<Organization>
  isDisplayInteractive: Signal<boolean>
  airlines: Signal<Airline[]>
  allowPoSubmit: Signal<boolean>;
  folder: Signal<Folder>;
  breadcrumbs: Signal<BreadCrumbs>;
  assetpath: Signal<string>;
  view: WritableSignal<string> = signal<string>('listing');
  refreshing = signal<boolean>(false);
  private folderPath = '';
  private folderQueryRef?: QueryRef<Query>;
  // Guards against opening more than one "create home folder" dialog per navigation
  // (the folder query can re-emit the not-found error under cache-and-network).
  private homeFolderPrompted = false;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    public banner: BannerService,
    private apollo: Apollo,
    public poMetaService: PoMetaService,
    public organizationService: ContextService,
    private dialog: Dialog) {

    this.organization = this.organizationService.organization
    this.airlines = this.organizationService.airlines


    this.assetpath = toSignal(this.route.paramMap.pipe(
      map(pm => {
        return pm.get('assetpath') || '';
      }))
    )

    this.route.queryParamMap.pipe(
      map(params => params.get('view') || '')
    ).subscribe(view => this.view.set(view));

    // The Display-Interactive group: the parent org and every airline sub-org under it. A legacy_name
    // holds exactly one underscore, separating parent from child, so the part before it names the
    // group. Matching the parent name alone hid the Display-Interactive submit forms from precisely
    // the operators who create the orders.
    this.isDisplayInteractive = computed(
      () => (this.organization()?.legacy_name ?? '').split('_')[0] === 'displayinteractive'
    );

    this.allowPoSubmit = computed(() => {
      const legacyName = this.organization()?.legacy_name;
      // Orgs that get the repackaging / display-interactive tabs (gated below)
      // must always be able to see the PO submit area.
      const isWorkflowVariantOrg = this.isDisplayInteractive() || legacyName === 'castlabs_cpteam';
      const a = this.organizationService.isAllowedAnywhere('contentplatform:workflow:Mutation/StartWorkflowDefault')();
      const b = this.organizationService.isAllowedAnywhere('contentplatform:workflow:Mutation/StartWorkflowVodDefault')();

      // StartWorkflowVodBatch is deliberately not consulted any more: the VOD Batch tab it opened
      // is gone, so a principal holding only that permission would be shown an empty submit area.
      return !!a || !!b || isWorkflowVariantOrg;
    });

    this.folder = toSignal(this.route.paramMap.pipe(
      map(pm => pm.get('assetpath') || ''),
      filter(brefix => !!brefix), // Ensure brefix is valid
      tap((brefix) => {
        this.refreshing.set(true);
        this.homeFolderPrompted = false;
        this.folderPath = `s3://${brefix}/`;
      }),
      switchMap(brefix => {
        this.folderQueryRef = this.apollo.watchQuery<Query>({
          query: FILE_FOLDER_LIST_WITH_ARCHIVE,
          variables: {id: `s3://${brefix}/`, show_deleted: true},
          nextFetchPolicy: 'cache-and-network',
          // 'all' surfaces a GraphQL error as `response.error` on the emission instead of throwing it,
          // so we can detect it in the tap below (onlyCompleteData then filters the errored emission out).
          errorPolicy: 'all'
        });
        return this.folderQueryRef.valueChanges.pipe(
          tap(response => {
            // The folder resolver returns this exact message when the S3 prefix is empty — a new
            // customer whose home folder hasn't been created yet. Offer to create it.
            if (response.error?.message?.includes('Folder does not exist')) {
              this.refreshing.set(false);
              this.promptCreateHomeFolder();
            }
          }),
          onlyCompleteData(),
          map(res => res.data.folder)
        );
      }),
      tap((folder) => {
        this.refreshing.set(false)
        if (folder && this.view() === '') {
          if (
            folder.folders.length === 0 &&
            folder.files.length < 100 &&
            folder.files.length > 0 &&
            folder.files.some(f => f.tracks && f.tracks.length > 0)
          ) {
            this.view.set('asset');
          } else {
            this.view.set('listing');
          }
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {view: this.view()},
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
      }),
      shareReplay(1) // Ensures no unnecessary re-subscriptions
    ));


    this.breadcrumbs = computed(() => {
      const folder = this.folder();
      const org = this.organization();

      if (!org || !folder || !folder.parents) {
        return [];
      }

      const crumbs: BreadCrumb[] = [
        {'commands': `/o/${org.id}`, 'display': 'Content Platform'} as BreadCrumb,
        {'commands': `/o/${org.id}/files`, 'display': 'Files'} as BreadCrumb
      ];

      crumbs.push(...folder.parents.map(f => ({
        'commands': `/o/${org.id}/files/browse/${f.id.replace('s3://', '')}`,
        'display': f.name
      })));


      return crumbs;
    });

  }

  // A new customer's home folder doesn't exist yet — offer to create it in a confirm dialog.
  private promptCreateHomeFolder() {
    if (this.homeFolderPrompted) {
      return;
    }
    this.homeFolderPrompted = true;
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: 'Welcome!',
        message: "It looks like this is your first time here — this folder doesn't exist yet. Shall we create it for you?",
        actionName: 'Create folder',
        action: () => this.createFolder(),
      }
    });
  }

  createFolder() {
    this.refreshing.set(true);
    this.apollo.mutate({
      mutation: MKDIR,
      variables: {folder_id: this.folderPath}
    }).subscribe({
      next: () => {
        this.folderQueryRef?.refetch();
      },
      error: (err) => {
        this.refreshing.set(false);
        this.banner.open(`Could not create folder: ${err?.message ?? 'unknown error'}`, ['OK']);
      }
    });
  }

}
